import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms';
import { cn } from '@/lib/utils';
import { FilePlus2, FileSpreadsheet } from 'lucide-react';

interface AddProductMethodModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddManually: () => void;
  onBulkUpload: () => void;
}

const methodOptions = [
  {
    id: 'manual',
    title: 'Add Manually',
    description: 'Enter product details yourself.',
    icon: FilePlus2,
  },
  {
    id: 'bulk',
    title: 'Bulk Upload',
    description: 'Already have a list? Upload multiple products at once.',
    icon: FileSpreadsheet,
  },
] as const;

export const AddProductMethodModal = ({
  open,
  onOpenChange,
  onAddManually,
  onBulkUpload,
}: AddProductMethodModalProps) => {
  const handleSelect = (method: (typeof methodOptions)[number]['id']) => {
    onOpenChange(false);
    if (method === 'manual') {
      onAddManually();
      return;
    }
    onBulkUpload();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-6 sm:max-w-2xl">
        <DialogHeader className="text-center sm:text-center">
          <DialogTitle className="text-xl">
            Choose how to add your products
          </DialogTitle>
          <DialogDescription>
            Add products manually one by one, or bulk upload many at once with
            our spreadsheet template.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          {methodOptions.map((option) => {
            const Icon = option.icon;

            return (
              <button
                key={option.id}
                type="button"
                onClick={() => handleSelect(option.id)}
                className={cn(
                  'flex flex-col items-start gap-4 rounded-xl border bg-white p-5 text-left transition-colors',
                  'hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30'
                )}
              >
                <div className="rounded-lg bg-primary/10 p-2.5 text-primary">
                  <Icon className="size-5" />
                </div>
                <div className="space-y-1">
                  <p className="font-semibold text-foreground">
                    {option.title}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {option.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          Add products later
        </button>
      </DialogContent>
    </Dialog>
  );
};
